/**
 * همه‌ی اعلان‌ها، خوانده و نخوانده (WHOSUP-131) — قرینه‌ی
 * apps/mobile/app/notifications.tsx. راهش از صفحه‌ی پروفایل است.
 *
 * سه قرار، هر سه همان قرارِ لمسِ پوش:
 *   • متن با `notifTitle`/`notifBody` از کلید ساخته می‌شود، نه از ستونِ
 *     متن — وگرنه خبرِ قدیمی به زبانِ روزِ ساخته‌شدنش می‌ماند.
 *   • کلیک همان یک سطر را خوانده می‌کند (مایگریشن 0118)، حتی اقدام‌خواه‌ها
 *     را — کلیکِ عمدی «دیدم» است، برخلافِ صرفِ باز کردنِ صفحه (markSeen).
 *   • مقصد فقط از `data`ی سطر می‌آید — `core/lib/notifTarget.ts`.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationRepo } from '@/core/data/miscRepo';
import { notifWebTarget } from '@/core/lib/notifTarget';
import { notifBody, notifTitle } from '@/core/lib/notifText';
import { formatDateTime } from '@/core/lib/dates';
import { t, useLocale } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { useSession } from '@/core/session';
import type { AppNotification } from '@/core/domain/types';

/**
 * قدِ هر صفحه. «موارد بیشتر» سقف را بالا می‌برد و همان کوئری از نو می‌آید —
 * صفحه‌بندیِ واقعی نیست، ولی برای جدولی که به‌ازای هر کاربر چند صد سطر
 * بیشتر نمی‌شود، یک درخواستِ بزرگ‌شونده ساده‌تر از نگه داشتنِ صفحه‌هاست.
 */
const PAGE = 50;

export function NotificationsPage() {
  useLocale();
  const { profile } = useSession();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [limit, setLimit] = useState(PAGE);

  const list = useQuery({
    queryKey: ['notifHistory', limit],
    queryFn: () => notificationRepo.list(profile!.id, { limit }),
    enabled: !!profile,
    // موقعِ «موارد بیشتر»، فهرستِ فعلی بماند تا صفحه خالی پلک نزند.
    placeholderData: (prev: AppNotification[] | undefined) => prev,
  });

  const open = (n: AppNotification) => {
    if (!n.readAt) {
      notificationRepo.markRead(n.id)
        .then(() => Promise.all([
          qc.invalidateQueries({ queryKey: ['notifHistory'] }),
          qc.invalidateQueries({ queryKey: ['unreadByCategory'] }),
          qc.invalidateQueries({ queryKey: ['notifications'] }),
        ]))
        .catch((e) => reportError('web.notifHistory.markRead', e, { id: n.id }));
    }
    const target = notifWebTarget(n.data);
    if (target) nav(target);
  };

  const rows = list.data ?? [];

  return (
    <div className="feed">
      <h1 className="feed-title">{t('notifHistory')}</h1>

      {list.isLoading ? <p className="dim">…</p> : null}
      {!list.isLoading && rows.length === 0 ? <p className="dim">{t('noNotifications')}</p> : null}

      <div className="notif-list">
        {rows.map((n) => {
          const body = notifBody(n);
          const target = notifWebTarget(n.data);
          return (
            <button key={n.id} type="button"
              className={`notif-row${n.readAt ? '' : ' unread'}`}
              // سطرِ بی‌مقصد و خوانده هیچ کاری ندارد؛ دکمه‌ی خاموش صادق‌تر
              // از کلیکی است که هیچ اتفاقی نمی‌افتد.
              disabled={!target && !!n.readAt}
              onClick={() => open(n)}>
              <span className="notif-title">
                {n.readAt ? null : <span className="notif-dot" aria-hidden />}
                {notifTitle(n)}
              </span>
              {body ? <span className="dim">{body}</span> : null}
              <span className="notif-when">{formatDateTime(n.createdAt)}</span>
            </button>
          );
        })}
      </div>

      {/* صفحه‌ی پُر یعنی احتمالاً باز هم هست — همان قاعده‌ی warnIfFull. */}
      {rows.length === limit ? (
        <button type="button" className="btn ghost"
          onClick={() => setLimit((l) => l + PAGE)}>
          {t('loadMore')}
        </button>
      ) : null}
    </div>
  );
}
